import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiError } from '@/lib/api.js';

const TOTAL_GWS = 38;
const MIN_WINDOW = 3;
const MAX_WINDOW = 9;
const DEFAULT_WINDOW = 6;

/*
 * Fixture ticker for one FDR profile.
 *
 * The payload is {headers, start, ticker} where each ticker row is
 * [teamShort, [[gwNumber, [{opp, fdr}, ...]], ...]]. A gameweek can hold
 * more than one fixture (a double), or an entry with an empty opponent
 * (a blank). Opponent case carries the venue: uppercase is home, lowercase
 * away — see helper.get_ticker.
 */
export function useTicker(user = 'nutmeg') {
	const [data, setData] = useState(null);
	const [status, setStatus] = useState('loading');
	const [error, setError] = useState(null);

	const [start, setStart] = useState(0);
	const [size, setSize] = useState(DEFAULT_WINDOW);
	const [sortByDifficulty, setSortByDifficulty] = useState(true);

	useEffect(() => {
		let cancelled = false;
		setStatus('loading');
		api
			.get(`/api/ticker/${user}`)
			.then((payload) => {
				if (cancelled) return;
				if (!payload?.ticker?.length) {
					setError('No ticker data available.');
					setStatus('error');
					return;
				}
				setData(payload);
				/* `start` is currentGW + 1, which runs past the end of the
				 * season once GW38 is played. Clamp so the ticker still shows
				 * a full window between seasons instead of nothing. */
				const requested = (payload.start ?? 1) - 1;
				setStart(Math.max(0, Math.min(requested, TOTAL_GWS - DEFAULT_WINDOW)));
				setStatus('ready');
			})
			.catch((err) => {
				if (cancelled) return;
				setError(err instanceof ApiError ? err.message : 'Could not load the ticker.');
				setStatus('error');
			});
		return () => {
			cancelled = true;
		};
	}, [user]);

	const end = Math.min(start + size, TOTAL_GWS);

	/* Each edge can move independently: growing from one side extends the
	 * window until it hits the maximum, then slides it. */
	const adjust = useCallback(
		(side, delta) => {
			if (delta > 0) {
				if (side === 'start') {
					setStart((s) => {
						if (s <= 0) return s;
						setSize((w) => (w < MAX_WINDOW ? w + 1 : w));
						return s - 1;
					});
				} else {
					setSize((w) => {
						if (start + w >= TOTAL_GWS) return w;
						if (w < MAX_WINDOW) return w + 1;
						setStart((s) => Math.min(s + 1, TOTAL_GWS - w));
						return w;
					});
				}
			} else {
				if (size <= MIN_WINDOW) return;
				setSize((w) => w - 1);
				if (side === 'start') setStart((s) => s + 1);
			}
		},
		[start, size],
	);

	const rows = useMemo(() => {
		if (!data) return [];
		const withAverage = data.ticker.map(([team, gws]) => {
			const window = gws.slice(start, end);
			const fdrs = window.flatMap(([, fixtures]) =>
				fixtures.filter((f) => f.opp).map((f) => f.fdr),
			);
			return {
				team,
				gameweeks: window,
				/* Blanks score as the hardest possible run: no fixture is worse
				 * for a ticker than one you cannot field a player in. */
				average: fdrs.length ? fdrs.reduce((a, b) => a + b, 0) / fdrs.length : 6,
				fixtureCount: fdrs.length,
			};
		});
		return sortByDifficulty
			? [...withAverage].sort((a, b) => a.average - b.average || a.team.localeCompare(b.team))
			: [...withAverage].sort((a, b) => a.team.localeCompare(b.team));
	}, [data, start, end, sortByDifficulty]);

	return {
		status,
		error,
		rows,
		headers: data?.headers?.slice(start, end) ?? [],
		start,
		end,
		size,
		canGrowStart: start > 0,
		canGrowEnd: end < TOTAL_GWS,
		canShrink: size > MIN_WINDOW,
		adjust,
		sortByDifficulty,
		setSortByDifficulty,
	};
}
